import type {
  CoachRuntimeServiceReadModel,
  CoachServiceStopRequest,
  OperatorIdentity,
  RequestCoachServiceStopRequest,
} from "@billiard/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Clock3, Hand, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  listCoachServiceStopRequests,
  requestCoachServiceStop,
  transitionCoachService,
} from "./api";
import { businessErrorMessage } from "./businessErrorMessage";
import {
  beginCoachServiceStopRequestDispatch,
  type CoachServiceStopFixedCommand,
  type CoachServiceStopRequestRecovery,
  claimCoachServiceStopRequestRecovery,
  clearCoachServiceStopRequestRecovery,
  coachServiceStopRequestContext,
  coachServiceStopRequestOutcomeIsUnknown,
  finishCoachServiceStopRequestDispatch,
  markCoachServiceStopRequestOutcomeUnknown,
  readCoachServiceStopRequestRecovery,
} from "./coachServiceStopRequestRecovery";
import "./CoachServiceStopRequestPanel.css";

type StopRequestMode = "SELF_COACH" | "CASHIER" | "MANAGER" | "OWNER_READ_ONLY" | "HIDDEN";

export function coachServiceStopRequestMode(
  operator: Pick<OperatorIdentity, "role">,
  effectiveRoles: readonly OperatorIdentity["role"][],
): StopRequestMode {
  if (operator.role === "OWNER") return "OWNER_READ_ONLY";
  if (operator.role === "WAITER") return "HIDDEN";
  if (operator.role === "COACH" && effectiveRoles.includes("COACH")) return "SELF_COACH";
  if (operator.role === "MANAGER" && effectiveRoles.includes("MANAGER")) return "MANAGER";
  if (operator.role === "CASHIER" && effectiveRoles.includes("CASHIER")) return "CASHIER";
  return "HIDDEN";
}

function timeLabel(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function stopRequestStatusCopy(request: CoachServiceStopRequest): {
  label: string;
  explanation: string;
} {
  if (request.status === "PENDING") {
    return {
      label: "等待正式下钟",
      explanation: "这只是申请，不会停止计时或结算。收银员或店长确认后才正式下钟。",
    };
  }
  if (request.status === "COMPLETED") {
    return {
      label: "已处理并下钟",
      explanation: `${request.resolvedByDisplayName ?? "当班员工"}已完成正式下钟${
        request.resolutionNote ? `：${request.resolutionNote}` : "。"
      }`,
    };
  }
  return {
    label: "申请已取消",
    explanation: request.resolutionNote ?? "本次申请没有执行下钟。",
  };
}

function newKey(prefix: string): string {
  return `${prefix}:${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`}`;
}

export function CoachServiceStopRequestPanel({
  operator,
  effectiveRoles,
  activeServices = [],
  scopeServiceIds,
  onCompleted,
  onRecoveryBlockedChange,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorIdentity["role"][];
  activeServices?: readonly CoachRuntimeServiceReadModel[];
  scopeServiceIds?: readonly string[];
  onCompleted?: () => void | Promise<void>;
  onRecoveryBlockedChange?: (blocked: boolean) => void;
}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const mode = coachServiceStopRequestMode(operator, effectiveRoles);
  const [requestReason, setRequestReason] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [feedback, setFeedback] = useState<{ tone: "INFO" | "ERROR"; text: string } | null>(null);
  const [pending, setPending] = useState(false);
  const [recoveryRevision, setRecoveryRevision] = useState(0);
  const requestFlight = useRef(false);
  const recoveryContext = coachServiceStopRequestContext(operator);
  void recoveryRevision;
  const recoveryView = readCoachServiceStopRequestRecovery(recoveryContext);
  const recoveryBlocksOtherOperations = recoveryView.status !== "NONE";
  const recoveryLockedRequestId =
    recoveryView.status === "OWNED" && recoveryView.recovery.command.kind === "COMPLETE_STOP"
      ? recoveryView.recovery.command.stopRequest.requestId
      : null;
  useEffect(() => {
    onRecoveryBlockedChange?.(recoveryBlocksOtherOperations);
  }, [onRecoveryBlockedChange, recoveryBlocksOtherOperations]);
  const stopRequests = useQuery({
    queryKey: ["coach-service-stop-requests", operator.tenantId, operator.storeId, operator.id],
    queryFn: listCoachServiceStopRequests,
    enabled:
      mode !== "HIDDEN" &&
      recoveryView.status !== "CONTEXT_MISMATCH" &&
      recoveryView.status !== "STORAGE_UNAVAILABLE" &&
      recoveryView.status !== "COMMAND_MISMATCH",
    retry: false,
    refetchInterval: 2_000,
  });
  const scope = useMemo(
    () => (scopeServiceIds ? new Set(scopeServiceIds) : null),
    [scopeServiceIds],
  );
  const visibleRequests = (stopRequests.data?.items ?? []).filter(
    (request) => scope === null || scope.has(request.serviceId),
  );
  const ownActiveServices = activeServices.filter(
    (service) => service.status === "ACTIVE" && service.coachId === operator.coachProfileId,
  );
  const ownActiveService = ownActiveServices[0] ?? null;
  const ownLatestRequest = visibleRequests.find(
    (request) => request.coachId === operator.coachProfileId,
  );
  if (mode === "HIDDEN") return null;

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["coach-service-stop-requests"] }),
      queryClient.invalidateQueries({ queryKey: ["coach-runtime"] }),
      queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
    ]);
    await onCompleted?.();
  };

  const recoveryFailure = (message: string): void => {
    setFeedback({ tone: "ERROR", text: message });
    setRecoveryRevision((current) => current + 1);
  };

  const dispatchFixedCommand = async (recovery: CoachServiceStopRequestRecovery): Promise<void> => {
    if (requestFlight.current) return;
    if (!beginCoachServiceStopRequestDispatch(recovery, recoveryContext)) {
      recoveryFailure(
        "原操作无法安全核对，当前没有发送请求。请不要新建另一笔操作，联系技术支持。 ",
      );
      return;
    }
    requestFlight.current = true;
    setPending(true);
    setFeedback({
      tone: "INFO",
      text:
        recovery.command.kind === "REQUEST_STOP"
          ? "正在提交申请。提交后仍继续计时，等待收银员或店长正式下钟。"
          : "正在正式下钟。完成前请勿重复点击，服务费用只会结算一次。",
    });
    try {
      if (recovery.command.kind === "REQUEST_STOP") {
        await requestCoachServiceStop(
          recovery.command.serviceId,
          recovery.command.request,
          recovery.command.expected,
        );
        setRequestReason("");
        setFeedback({
          tone: "INFO",
          text: "申请已进入待办。当前服务仍在继续，不会提前计费或自动下钟。",
        });
      } else {
        await transitionCoachService(
          recovery.command.serviceId,
          recovery.command.action,
          recovery.command.reason,
          recovery.command.idempotencyKey,
          recovery.command.stopRequest,
        );
        setSelectedRequestId("");
        setResolutionNote("");
        setFeedback({ tone: "INFO", text: "正式下钟已完成，服务时间、费用和处理人已经记录。" });
      }
      finishCoachServiceStopRequestDispatch(recovery, recoveryContext);
      if (!clearCoachServiceStopRequestRecovery(recovery, recoveryContext)) {
        recoveryFailure(
          "操作已完成，但本机无法清除恢复记录。请先不要再次操作，联系技术支持核对。 ",
        );
        return;
      }
      await refresh();
    } catch (error) {
      finishCoachServiceStopRequestDispatch(recovery, recoveryContext);
      const outcomeUnknown = coachServiceStopRequestOutcomeIsUnknown(error);
      if (outcomeUnknown) {
        if (!markCoachServiceStopRequestOutcomeUnknown(recovery, recoveryContext)) {
          recoveryFailure(
            "操作结果暂时无法确认，而且本机无法安全保存原操作。请不要重复操作，联系技术支持。 ",
          );
          return;
        }
      } else if (!clearCoachServiceStopRequestRecovery(recovery, recoveryContext)) {
        recoveryFailure(
          "操作确定没有执行，但本机无法清除恢复记录。请不要新建另一笔操作，联系技术支持。 ",
        );
        return;
      }
      const subject = recovery.command.kind === "REQUEST_STOP" ? "申请" : "下钟";
      setFeedback({
        tone: "ERROR",
        text: outcomeUnknown
          ? `${subject}结果暂时无法确认：${businessErrorMessage(error, "请刷新状态后核对")}。系统已保存原操作，再次尝试只会使用同一个操作号。`
          : `${subject}没有执行：${businessErrorMessage(error, "请按提示修改后再试")}。`,
      });
    } finally {
      requestFlight.current = false;
      setPending(false);
      setRecoveryRevision((current) => current + 1);
    }
  };

  const claimAndDispatch = async (command: CoachServiceStopFixedCommand): Promise<void> => {
    const claimed = claimCoachServiceStopRequestRecovery(recoveryContext, command);
    setRecoveryRevision((current) => current + 1);
    if (claimed.status === "CONTEXT_MISMATCH") {
      recoveryFailure(
        "另一个账号或门店有一笔下钟操作等待确认。当前账号不能查看或重试，请切回原账号处理。 ",
      );
      return;
    }
    if (claimed.status === "COMMAND_MISMATCH") {
      recoveryFailure("已有一笔下钟操作等待确认。请先恢复原操作，当前没有发送新请求。 ");
      return;
    }
    if (claimed.status === "STORAGE_UNAVAILABLE" || claimed.status === "NONE") {
      recoveryFailure(
        "本机无法安全保存下钟操作，当前没有发送请求。请不要重复点击，联系技术支持。 ",
      );
      return;
    }
    await dispatchFixedCommand(claimed.recovery);
  };

  const submitSelfRequest = async (): Promise<void> => {
    if (!ownActiveService || requestFlight.current || recoveryView.status !== "NONE") return;
    const normalizedReason = requestReason.trim();
    if (normalizedReason.length < 2 || normalizedReason.length > 500) {
      setFeedback({ tone: "ERROR", text: "请填写 2 至 500 个字，说明为什么现在需要下钟。" });
      return;
    }
    const request: RequestCoachServiceStopRequest = {
      expectedServiceVersion: ownActiveService.version,
      reason: normalizedReason,
      idempotencyKey: newKey("coach-stop-request"),
    };
    await claimAndDispatch({
      kind: "REQUEST_STOP",
      serviceId: ownActiveService.id,
      request,
      expected: {
        coachId: ownActiveService.coachId,
        tableNumber: ownActiveService.tableNumber,
        businessDate: ownActiveService.businessDate,
      },
    });
  };

  const resolveStopRequest = async (request: CoachServiceStopRequest): Promise<void> => {
    if (requestFlight.current || recoveryView.status !== "NONE") return;
    const normalizedNote = resolutionNote.trim();
    if (normalizedNote.length < 2 || normalizedNote.length > 500) {
      setFeedback({
        tone: "ERROR",
        text:
          mode === "MANAGER"
            ? "店长代办必须填写 2 至 500 个字，说明代办原因和现场核对结果。"
            : "请填写 2 至 500 个字，说明已经核对的下钟时间和现场情况。",
      });
      return;
    }
    await claimAndDispatch({
      kind: "COMPLETE_STOP",
      serviceId: request.serviceId,
      action: "COMPLETE",
      reason: normalizedNote,
      stopRequest: request,
      idempotencyKey: newKey("coach-stop-complete"),
    });
  };

  const recoveryNotice = (() => {
    if (recoveryView.status === "NONE") return null;
    if (recoveryView.status === "CONTEXT_MISMATCH") {
      return (
        <div className="error-band" role="alert">
          另一个账号或门店有一笔下钟操作等待确认。当前账号不能查看内容或重试，请切回原账号处理。
        </div>
      );
    }
    if (recoveryView.status === "STORAGE_UNAVAILABLE") {
      return (
        <div className="error-band" role="alert">
          本机无法安全核对下钟操作。为避免重复计费，已暂停新的申请和下钟，请联系技术支持。
        </div>
      );
    }
    if (recoveryView.status === "COMMAND_MISMATCH") {
      return (
        <div className="error-band" role="alert">
          已有一笔下钟操作等待确认，当前没有发送新操作。
        </div>
      );
    }
    return (
      <div className="coach-stop-recovery" aria-live="polite">
        <strong>上一笔操作的结果需要确认</strong>
        <p>
          请先刷新业务状态。系统只允许按原内容和原操作号安全重试；如果上次已经成功，只会返回原处理结果，不会重复申请或结算。
        </p>
        <button
          className="button secondary"
          type="button"
          disabled={pending || !recoveryView.canRetry || mode === "OWNER_READ_ONLY"}
          onClick={() => void dispatchFixedCommand(recoveryView.recovery)}
        >
          {pending ? "正在核对…" : "按原操作安全重试"}
        </button>
      </div>
    );
  })();

  if (mode === "SELF_COACH") {
    const currentPending = visibleRequests.find(
      (request) => request.status === "PENDING" && request.serviceId === ownActiveService?.id,
    );
    return (
      <section className="coach-stop-request-panel" aria-label="我的下钟申请">
        <div className="coach-stop-request-heading">
          <div>
            <h3>申请下钟</h3>
            <p>申请只会通知收银员和店长，不会立即停止计时、结算费用或完成下钟。</p>
          </div>
          <Hand size={20} aria-hidden="true" />
        </div>
        {recoveryNotice}
        {stopRequests.isPending ? <p>正在读取你的下钟申请…</p> : null}
        {stopRequests.isError ? (
          <div className="error-band" role="alert">
            下钟申请状态暂时无法读取，请刷新后再试；系统不会把未知状态当作未申请。
          </div>
        ) : null}
        {stopRequests.data?.terminalHistoryTruncated ? (
          <output className="coach-form-note">
            待处理申请已全部列出；更早已处理记录未在本页展示。
          </output>
        ) : null}
        {currentPending ? (
          <article className="coach-stop-request-card">
            <header>
              <h4>等待收银员或店长处理</h4>
              <span className="status-pill is-warning">待处理</span>
            </header>
            <p>{stopRequestStatusCopy(currentPending).explanation}</p>
            <small>
              申请单号 {currentPending.requestNumber} · {currentPending.tableNumber} 号桌 ·{" "}
              {timeLabel(currentPending.requestedAt)}
            </small>
            <p>你的原因：{currentPending.reason}</p>
          </article>
        ) : ownActiveService ? (
          <form
            className="coach-stop-request-form"
            name="coach-self-stop-request"
            onSubmit={(event) => {
              event.preventDefault();
              void submitSelfRequest();
            }}
          >
            <label>
              申请原因
              <textarea
                name="coach-stop-request-reason"
                value={requestReason}
                maxLength={500}
                disabled={pending || recoveryView.status !== "NONE"}
                placeholder="例如：本次服务已完成，请收银员核对后下钟"
                onChange={(event) => setRequestReason(event.target.value)}
              />
              <small>填写 2 至 500 个字。提交后仍继续服务和计时，直到有权限员工确认。</small>
            </label>
            <button
              className="button secondary"
              type="submit"
              disabled={
                pending || recoveryView.status !== "NONE" || requestReason.trim().length < 2
              }
            >
              <Clock3 size={17} aria-hidden="true" />
              {pending ? "正在提交…" : "提交下钟申请"}
            </button>
          </form>
        ) : (
          <p>当前没有正在服务的任务，不能新建下钟申请。</p>
        )}
        {ownLatestRequest && ownLatestRequest.status !== "PENDING" ? (
          <article className="coach-stop-request-card">
            <header>
              <h4>{stopRequestStatusCopy(ownLatestRequest).label}</h4>
              <BadgeCheck size={19} aria-hidden="true" />
            </header>
            <p>{stopRequestStatusCopy(ownLatestRequest).explanation}</p>
            <small>
              申请单号 {ownLatestRequest.requestNumber} · 处理于{" "}
              {ownLatestRequest.resolvedAt ? timeLabel(ownLatestRequest.resolvedAt) : "—"}
            </small>
          </article>
        ) : null}
        {feedback ? (
          <output
            className={feedback.tone === "ERROR" ? "error-band" : "coach-form-note"}
            role={feedback.tone === "ERROR" ? "alert" : "status"}
          >
            {feedback.text}
          </output>
        ) : null}
      </section>
    );
  }

  return (
    <section className="coach-stop-request-panel" aria-label="下钟申请待办">
      <div className="coach-stop-request-heading">
        <div>
          <h3>{mode === "OWNER_READ_ONLY" ? "下钟申请记录" : "待处理下钟申请"}</h3>
          <p>
            {mode === "OWNER_READ_ONLY"
              ? "老板可查看申请和处理结果；日常下钟由收银员或店长办理。"
              : "助教提交申请后仍继续计时。核对现场和服务时间后，才在这里正式下钟。"}
          </p>
        </div>
        <button
          className="button secondary"
          type="button"
          onClick={() => void stopRequests.refetch()}
          disabled={stopRequests.isFetching}
        >
          <RefreshCw size={17} aria-hidden="true" /> 刷新待办
        </button>
      </div>
      {recoveryNotice}
      {stopRequests.isPending ? <p>正在读取下钟申请…</p> : null}
      {stopRequests.isError ? (
        <div className="error-band" role="alert">
          下钟申请读取失败，当前不会显示或处理旧数据；请刷新后再试。
        </div>
      ) : null}
      {stopRequests.data?.terminalHistoryTruncated ? (
        <output className="coach-form-note">
          待处理申请已全部列出；更早已处理记录未在本页展示。
        </output>
      ) : null}
      {!stopRequests.isPending && !stopRequests.isError && visibleRequests.length === 0 ? (
        <p>当前没有下钟申请。</p>
      ) : null}
      <ul className="coach-stop-request-list">
        {visibleRequests.map((request) => {
          const copy = stopRequestStatusCopy(request);
          const selected = selectedRequestId === request.requestId;
          const recoveryLocked = recoveryLockedRequestId === request.requestId;
          return (
            <li
              className={`coach-stop-request-card${recoveryLocked ? " is-recovery-locked" : ""}`}
              key={request.requestId}
            >
              <header>
                <div>
                  <h4>
                    {request.coachDisplayName} · {request.tableNumber} 号桌
                  </h4>
                  <small>申请单号 {request.requestNumber}</small>
                </div>
                <span
                  className={`status-pill ${request.status === "PENDING" ? "is-warning" : "is-active"}`}
                >
                  {copy.label}
                </span>
              </header>
              <dl className="coach-stop-request-facts">
                <div>
                  <dt>助教申请原因</dt>
                  <dd>{request.reason}</dd>
                </div>
                <div>
                  <dt>申请时间</dt>
                  <dd>{timeLabel(request.requestedAt)}</dd>
                </div>
              </dl>
              <p>{copy.explanation}</p>
              {recoveryLocked ? (
                <output className="coach-stop-request-lock-note">
                  <strong>这就是结果待确认的原申请</strong>
                  <span>其他待办已锁定。请只使用上方“按原操作安全重试”核对结果。</span>
                </output>
              ) : null}
              {request.status === "PENDING" && mode !== "OWNER_READ_ONLY" ? (
                selected ? (
                  <form
                    className="coach-stop-request-form"
                    name={`coach-stop-request-resolve-${request.requestId}`}
                    onSubmit={(event) => {
                      event.preventDefault();
                      void resolveStopRequest(request);
                    }}
                  >
                    <label>
                      {mode === "MANAGER" ? "店长代办原因和核对结果" : "下钟核对说明"}
                      <textarea
                        name="coach-stop-resolution-note"
                        value={resolutionNote}
                        maxLength={500}
                        disabled={pending || recoveryView.status !== "NONE"}
                        placeholder={
                          mode === "MANAGER"
                            ? "例如：晚班收银员临时缺席，已现场核对服务时间"
                            : "例如：已与助教和顾客核对服务时间"
                        }
                        onChange={(event) => setResolutionNote(event.target.value)}
                      />
                      <small>
                        {mode === "MANAGER"
                          ? "必须说明为什么由店长代办，以及已经核对了什么。"
                          : "确认后才会停止计时并结算本次服务，操作人会永久留痕。"}
                      </small>
                    </label>
                    <button
                      className="button primary"
                      type="submit"
                      disabled={
                        pending ||
                        recoveryView.status !== "NONE" ||
                        resolutionNote.trim().length < 2
                      }
                    >
                      <BadgeCheck size={17} aria-hidden="true" />{" "}
                      {pending ? "正在正式下钟…" : "确认正式下钟"}
                    </button>
                  </form>
                ) : (
                  <button
                    className="button primary"
                    type="button"
                    disabled={pending || recoveryView.status !== "NONE"}
                    onClick={() => {
                      setSelectedRequestId(request.requestId);
                      setResolutionNote("");
                      setFeedback(null);
                    }}
                  >
                    {recoveryView.status === "NONE"
                      ? "核对并正式下钟"
                      : recoveryLocked
                        ? "原操作结果待确认"
                        : "先确认上一笔操作"}
                  </button>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
      {feedback ? (
        <output
          className={feedback.tone === "ERROR" ? "error-band" : "coach-form-note"}
          role={feedback.tone === "ERROR" ? "alert" : "status"}
        >
          {feedback.text}
        </output>
      ) : null}
    </section>
  );
}
